/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect, useRef } from 'react';
import Model from '@tripian/model';
import Button from '../base/Button/Button';
import BUTTON_TYPES from '../base/Button/ButtonTypes';

export type SuggestedCityMatch = {
  city: {
    id: string;
    name: string;
    description: string;
  };
  reason: string;
  match_score: number;
  highlights: string[];
};

interface ICitySearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  suggestions: SuggestedCityMatch[];
  onSelectCity: (cityId: number) => void;
  searchText: string;
  handleSearchChange: (text: string, isQuickSearch?: boolean) => void;
  isSearching: boolean;
  onClearSuggestions?: () => void;
  t: (value: Model.TranslationKey) => string;
}

const CitySearchModal: React.FC<ICitySearchModalProps> = ({ isOpen, onClose, suggestions, onSelectCity, searchText, handleSearchChange, isSearching, onClearSuggestions, t }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalSearchQuery, setModalSearchQuery] = useState(searchText || '');
  const [localIsSearching, setLocalIsSearching] = useState(isSearching || false);
  const [hasSearched, setHasSearched] = useState(false);
  const [internalSuggestions, setInternalSuggestions] = useState(suggestions || []);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalIsSearching(true);
    setHasSearched(true);

    // Call the parent's search handler
    if (handleSearchChange) {
      handleSearchChange(modalSearchQuery);
    }
  };

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Update local state when props change
  useEffect(() => {
    setLocalIsSearching(isSearching || false);
    setInternalSuggestions(suggestions || []);
  }, [isSearching, suggestions]);

  const handleQuickSearch = (query: string) => {
    setModalSearchQuery(query);
    setLocalIsSearching(true);
    setHasSearched(true);

    // Call the parent's search handler
    if (handleSearchChange) {
      handleSearchChange(query);
    }
  };

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Focus input on modal open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const inputElement = modalRef.current?.querySelector('input');
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  // Handle city selection including keyboard events
  const handleCitySelection = (cityId: string) => {
    const cityIdNumber = parseInt(cityId, 10);
    if (onSelectCity) {
      onSelectCity(cityIdNumber);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, cityId: string) => {
    const cityIdNumber = parseInt(cityId, 10);
    // Select on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCitySelection(cityIdNumber.toString());
    }
  };

  // Render modal content based on state
  function renderModalContent() {
    // If there are results, show them regardless of search query length
    if (internalSuggestions.length > 0) {
      return (
        <div className="divide-y">
          {internalSuggestions.map((match) => (
            <div
              key={match.city.id}
              className="w-full text-left p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => handleCitySelection(match.city.id)}
              onKeyDown={(e) => handleKeyDown(e, match.city.id)}
              role="button"
              tabIndex={0}
              aria-label={`${t('destination.ai.result.select')} ${match.city.name}`}
            >
              <div className="flex items-start gap-4 w-full">
                <div className="flex-1">
                  <h4 className="font-medium text-lg flex items-center">
                    <span className="inline-block w-2 h-2 rounded-full bg-primary-color mr-2" />
                    {match.city.name}
                  </h4>
                  <p className="text-sm text-gray-600 line-clamp-2">{match.city.description}</p>
                  <div className="mt-2">
                    <div className="text-xs font-medium text-primary-color">{t('destination.ai.result.reasonLabel')}</div>
                    <p className="text-sm text-gray-700">{match.reason}</p>
                  </div>
                  {match.highlights.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {match.highlights.map((highlight) => (
                        <span key={`${match.city.id}-${highlight}`} className="px-2 py-1 bg-gray-100 text-xs rounded-full">
                          {highlight}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-sm font-medium text-primary-color">
                  {Math.round(match.match_score)}% {t('destination.ai.result.matchLabel')}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Show loading state if searching
    if (localIsSearching) {
      return (
        <div className="flex flex-col items-center justify-center p-12">
          <p className="text-lg font-medium text-gray-700">{t('destination.ai.searching.title')}</p>
          <p className="mt-2 text-sm text-gray-500">{t('destination.ai.searching.subtitle')}</p>
        </div>
      );
    }

    // Show no results if a search was performed but no results were found
    if (hasSearched) {
      return (
        <div className="p-8 text-center text-gray-500">
          <p className="text-lg font-medium">{t('destination.ai.noResults.message')}</p>
          <p className="text-sm mt-2">{t('destination.ai.noResults.suggestion')}</p>
        </div>
      );
    }

    // Only show intro screen if no results and query is short
    if (modalSearchQuery.length < 2) {
      return (
        <div className="p-8 text-center">
          <h3 className="text-xl font-medium mb-2">{t('destination.ai.modal.title')}</h3>
          <p className="text-gray-600 mb-4">{t('destination.ai.modal.description')}</p>
          <p className="text-sm text-gray-500">{t('destination.ai.modal.instruction')}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button
              onClick={() => handleQuickSearch(t('destination.ai.quickSearch.beach'))}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors"
              text={t('destination.ai.quickSearch.beach')}
              type={BUTTON_TYPES.TEXT}
            />
            <Button
              onClick={() => handleQuickSearch(t('destination.ai.quickSearch.history'))}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors"
              text={t('destination.ai.quickSearch.history')}
              type={BUTTON_TYPES.TEXT}
            />
            <Button
              onClick={() => handleQuickSearch(t('destination.ai.quickSearch.nature'))}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors"
              text={t('destination.ai.quickSearch.nature')}
              type={BUTTON_TYPES.TEXT}
            />
            <Button
              onClick={() => handleQuickSearch(t('destination.ai.quickSearch.food'))}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors"
              text={t('destination.ai.quickSearch.food')}
              type={BUTTON_TYPES.TEXT}
            />
            <Button
              onClick={() => handleQuickSearch(t('destination.ai.quickSearch.culture'))}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors"
              text={t('destination.ai.quickSearch.culture')}
              type={BUTTON_TYPES.TEXT}
            />
          </div>
        </div>
      );
    }

    // Default state - ready to search but query is valid length
    return (
      <div className="p-8 text-center">
        <h3 className="text-xl font-medium mb-2">{t('destination.ai.modal.title')}</h3>
        <p className="text-gray-600 mb-4">{t('destination.ai.modal.description')}</p>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col transition-transform duration-300 transform"
        style={{ opacity: isOpen ? 1 : 0, transform: isOpen ? 'scale(1)' : 'scale(0.95)' }}
      >
        <div className="p-4 border-b flex items-center justify-between">
          <h2 id="modal-title" className="text-lg font-semibold">
            {t('destination.ai.modal.headerTitle')}
          </h2>
          <button type="button" className="text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150 p-2" onClick={onClose} aria-label={t('destination.ai.modal.closeButton')}>
            ✕
          </button>
        </div>

        <div className="p-4 border-b">
          <form onSubmit={handleFormSubmit} className="flex flex-col gap-2">
            <div className="flex items-center border border-gray-300 rounded-full bg-white shadow-sm overflow-hidden">
              <input
                type="text"
                className="w-full p-3 border-0 focus:ring-0 outline-none pl-4"
                placeholder={t('destination.ai.modal.searchPlaceholder')}
                value={modalSearchQuery}
                onChange={(e) => setModalSearchQuery(e.target.value)}
                aria-label={t('destination.ai.modal.searchPlaceholder')}
              />
              <button
                type="submit"
                className="flex-shrink-0 flex items-center justify-center h-full px-4 text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                disabled={localIsSearching || modalSearchQuery.length < 1}
                aria-label={t('destination.ai.modal.searchButton')}
              >
                {localIsSearching ? <div className="animate-spin h-5 w-5 border-2 border-gray-500 rounded-full border-t-transparent" /> : <span className="text-lg font-bold">→</span>}
              </button>
            </div>
            <div className="mt-2 text-xs text-gray-500 text-right">{t('destination.ai.modal.pressEnter')}</div>

            <div className="flex justify-end mt-2 gap-2">
              {internalSuggestions.length > 0 && onClearSuggestions && (
                <button
                  type="button"
                  className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    setInternalSuggestions([]);
                    setHasSearched(false);
                    if (onClearSuggestions) {
                      onClearSuggestions();
                    }
                  }}
                >
                  {t('destination.ai.modal.clearButton')}
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="overflow-y-auto">{renderModalContent()}</div>
      </div>
    </div>
  );
};

export default CitySearchModal;
